import type { Signal } from "@lit-labs/signals";
import type { IRpcService } from "../rpc/types";
import type { CreatorItem } from "../creator-select/types";
import type { GetCoPromotersForEntityResult } from "api-contracts/rpc/co-promotion";
import type { ExistingCoPromoter } from "./save-collaborators";

interface CoPromoterTargetState<T extends { showCoPromotion: boolean }> {
  uiToggles: Signal.State<T>;
  collaboratorHandles: Signal.State<string[]>;
  existingCollaborators: Signal.State<CreatorItem[]>;
}

export interface LoadCoPromotersResult {
  loadedCoPromoters: ExistingCoPromoter[];
}

export async function loadCoPromoters<T extends { showCoPromotion: boolean }>(
  rpcService: IRpcService,
  entityType: string,
  entityId: string,
  state: CoPromoterTargetState<T>,
): Promise<LoadCoPromotersResult> {
  const result = await rpcService.call<GetCoPromotersForEntityResult>("get-co-promoters-for-entity", {
    entityType,
    entityId,
  });

  const validCoPromoters = result.coPromoters.filter((cp) => cp.promoterHandle !== null);
  const handles = validCoPromoters.map((cp) => cp.promoterHandle!);
  const items: CreatorItem[] = validCoPromoters.map((cp) => ({
    id: cp.promoterId,
    handle: cp.promoterHandle,
    imageUrl: cp.promoterAvatarUrl ?? undefined,
  }));

  state.collaboratorHandles.set(handles);
  state.existingCollaborators.set(items);
  if (validCoPromoters.length > 0) {
    state.uiToggles.set({ ...state.uiToggles.get(), showCoPromotion: true } as T);
  }

  return {
    loadedCoPromoters: validCoPromoters.map((cp) => ({
      handle: cp.promoterHandle!,
      coPromotionId: cp.coPromotionId,
    })),
  };
}
